import { createGlobalState } from '@vueuse/core'
import useVuelidate from '@vuelidate/core'
import { ECOUNTRIES } from '../enums/Countries'
import type { IMultiSportPartnerPayload } from '../types/MultiSportPartnerPayload.types'
import type { IMultiSportPartner } from '@/domains/partners/multisport/types/MultiSportPartner.types'
import { useNip } from '~/components/Inputs/Composables/validators/useNip/useNip'
import { useText } from '@/components/Inputs/Composables/validators/useText/useText'
import { usePhone, mapPrefixToCountry } from '~/components/Inputs/Composables/validators/usePhone/usePhone'
import { useEmail } from '~/components/Inputs/Composables/validators/useEmail/useEmail'
import { useSelect } from '~/components/Inputs/Composables/validators/useSelect/useSelect'
import { usePostalCode } from '~/components/Inputs/Composables/validators/usePostalCode/usePostalCode'
import { useWebsite } from '~/components/Inputs/Composables/validators/useWebsite/useWebsite'
import { useCheckbox } from '~/components/Inputs/Composables/validators/useCheckbox/useCheckbox'
import { useRadio } from '~/components/Inputs/Composables/validators/useRadio/useRadio'
import { ECountry } from '~/components/Inputs/Composables/validators/useNip/useNip.types'
import { useNumber } from '~/components/Inputs/Composables/validators/useNumber/useNumber'

const FORM_INITIAL_STATE: IMultiSportPartner = {
  firstName: '',
  lastName: '',
  email: '',
  companyRole: '',
  phonePrefix: '',
  phone: '',
  companyName: '',
  country: ECOUNTRIES.POLAND,
  nipNumber: '',
  street: '',
  buildingNumber: '',
  apartmentNumber: '',
  district: '',
  city: '',
  zipCode: '',
  workedWithBS: null,
  objectDistrict: null,
  objectName: '',
  objectArea: null,
  objectStreet: '',
  objectBuildingNumber: '',
  objectApartmentNumber: '',
  objectCountry: ECOUNTRIES.POLAND,
  objectCity: '',
  objectZipCode: '',
  objectWebiste: '',
  objectCategory: '',
  objectOtherCategory: '',
  objectPrefixPhone: '',
  objectPhone: '',
  objectHasOC: null,
  objectHasPermits: null,
  objectHasReception: null,
  objectAgree: false,
  objectAgreeRodo: false,
  objectAgreeBenefit: false,
  objectAgreeTelecommunications: false,
  objectAgreeMarketing: false,
  objectAcceptTerms: null,
  objectAcceptPrivacyPolicy: false,
  objectHasSafetyEquipment: null,
  objectIsAutonomous: null,
}

export const useMultisportPartnersStore = createGlobalState(() => {
  const { t } = useI18n()
  const form = reactive<IMultiSportPartner>(FORM_INITIAL_STATE)

  const getLocaleCode = computed(() => {
    return mapPrefixToCountry(form.phonePrefix.value)
  })

  const getLocaleCodeByObject = computed(() => {
    return mapPrefixToCountry(form.objectPrefixPhone.value)
  })

  const getCategory = computed(() => {
    return form.objectCategory.value
  })

  const $v = useVuelidate({
    ...useText('firstName', t, { length: 40, required: true }),
    ...useText('lastName', t, { length: 80, required: true }),
    ...usePhone('phone', getLocaleCode, t),
    ...useEmail('email', t),
    ...useSelect('phonePrefix', t),
    ...useSelect('companyRole', t),
    // step 2
    ...useSelect('district', t),
    ...useText('companyName', t, { length: 255, required: true }),
    ...useText('street', t, { length: 255, required: true }),
    ...useText('city', t, { length: 255, required: true }),
    ...useText('buildingNumber', t, { length: 10, required: true }),
    ...useRadio('workedWithBS', t, { isChecked: true }),
    ...useText('apartmentNumber', t, { length: 10, required: false }),
    ...usePostalCode('zipCode', 'PL', t),
    ...useNip('nipNumber', ECountry.POLAND, t),
    // step 3
    ...useText('objectName', t, { length: 255, required: true }),
    // there is a limit from Salesforce.
      
    ...useNumber('objectArea', t, { min: 1, max: 1000000, onlyNumeric: false, required: true,  numberType: 'Float' }),
    ...useSelect('objectDistrict', t),
    ...useText('objectStreet', t, { required: true, length: 255 }),
    ...useText('objectCity', t, { required: true, length: 255}),
    ...useText('objectBuildingNumber', t, { required: true, length: 10 }),
    ...useText('objectApartmentNumber', t, { required: false, length: 10 }),
    ...usePhone('objectPhone', getLocaleCodeByObject, t),
    ...useSelect('objectPrefixPhone', t),
    ...usePostalCode('objectZipCode', 'PL', t),
    ...useWebsite('objectWebiste', t),
    ...useSelect('objectCategory', t),
    ...useText('objectOtherCategory', t, { length: 255, required: false, requiredIf: () => getCategory.value === 'Inne' }),
    ...useRadio('objectHasOC', t, {isChecked: true}),
    ...useRadio('objectHasPermits', t, { isChecked: true}),
    ...useRadio('objectHasSafetyEquipment', t, { isChecked: true }),
    ...useRadio('objectHasReception', t, { isChecked: true }),
    ...useRadio('objectIsAutonomous', t, { isChecked: true }),
    ...useCheckbox('objectAcceptTerms', t, { required: true}),
  }, form)
  
  function getFieldValue(field: keyof IMultiSportPartner): string | number | boolean | { value: string; text: string } {
    return form[field]!
  }

  function parseFormPayload(form: IMultiSportPartner): IMultiSportPartnerPayload {
    let parsedForm: IMultiSportPartnerPayload = { ...form }

    Object.keys(form).forEach((key) => {
      const fieldValue = form[key as keyof IMultiSportPartner]

      if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
        // @ts-expect-error - bad type mapping
        parsedForm[key as keyof IMultiSportPartner] = (fieldValue as { value: number | string | boolean }).value
      }
    })

    const emptyStringToNull = (value?: string | null) : string | null => {
      if (!value || value.length === 0) {
        return null
      }
      return value
    }

    parsedForm.objectOtherCategory = emptyStringToNull(parsedForm.objectOtherCategory)
    parsedForm.apartmentNumber = emptyStringToNull(parsedForm.apartmentNumber)
    parsedForm.objectApartmentNumber = emptyStringToNull(parsedForm.objectApartmentNumber)

    return parsedForm
  }

  const resetForm = () => {
    Object.assign(form, FORM_INITIAL_STATE)
    $v.value.$reset()
  }

  return { form, getFieldValue, parseFormPayload, resetForm, $v }
})
